/**
 * Service Worker Registration Utility
 * Registers and manages service worker lifecycle
 */

import { isCloudDemoRuntime } from './publicDemoRuntime';
import i18n from '../../i18n';

/**
 * Register service worker
 * @param {Object} config - Configuration options
 * @returns {Promise<ServiceWorkerRegistration>}
 */
export const register = async (config = {}) => {
  const {
    onSuccess = null,
    onUpdate = null,
    onError = null
  } = config;

  if (!isCloudDemoRuntime()) {
    return null;
  }

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js?v=4.0.0');

      if (onSuccess) {
        onSuccess(registration);
      }

      // Reload once when a waiting worker takes control (fresh deploy after cache bump).
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
              if (onUpdate) onUpdate(registration);
            }
          }
        });
      });

      return registration;
    } catch (error) {
      if (onError) {
        onError(error);
      }
      return null;
    }
  }

  return null;
};

/**
 * Unregister service worker
 */
export const unregister = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    for (const registration of registrations) {
      await registration.unregister();
    }

    console.log('✅ Service Worker unregistered');
  }
};

/**
 * Check if service worker is available
 */
export const isSupported = () => {
  return 'serviceWorker' in navigator;
};

/**
 * Get service worker registration
 */
export const getRegistration = async () => {
  if ('serviceWorker' in navigator) {
    return await navigator.serviceWorker.ready;
  }
  return null;
};

/**
 * Clear all caches
 */
export const clearCaches = async () => {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log('✅ All caches cleared');
  }
};

/**
 * Get cache size
 */
export const getCacheSize = async () => {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    let totalSize = 0;

    for (const name of cacheNames) {
      const cache = await caches.open(name);
      const keys = await cache.keys();

      for (const request of keys) {
        const response = await cache.match(request);
        const blob = await response.blob();
        totalSize += blob.size;
      }
    }

    return totalSize;
  }
  return 0;
};

/**
 * Send message to service worker
 */
export const sendMessage = async (message) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
};

/**
 * Prompt user to update service worker
 */
export const promptUpdate = (registration) => {
  if (window.confirm(i18n.t('common.sw_update_confirm'))) {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }
};

export default {
  register,
  unregister,
  isSupported,
  getRegistration,
  clearCaches,
  getCacheSize,
  sendMessage,
  promptUpdate
};
